<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div>
        <h4 class="mb-0">
          Маршрутизация фронтенда
        </h4>
        <div class="text-muted small">
          Какие адреса сайта показывает старое приложение (legacy), а какие — новое (v4). Изменения применяются роутером в течение ~5 секунд.
        </div>
      </div>
      <b-button
        variant="outline-secondary"
        :disabled="loading || saving"
        @click="fetchState"
      >
        Обновить
      </b-button>
    </div>

    <b-alert
      v-if="errorText"
      show
      variant="danger"
      class="mb-1"
    >
      {{ errorText }}
    </b-alert>

    <b-card
      no-body
      class="mb-1"
    >
      <b-card-body class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="mb-50">
          <div class="d-flex align-items-center mb-50">
            <b-badge
              :variant="killSwitch ? 'danger' : 'success'"
              class="mr-1"
            >
              {{ killSwitch ? 'Kill switch ВКЛЮЧЁН' : 'Kill switch выключен' }}
            </b-badge>
            <span
              v-if="killSwitch"
              class="text-danger"
            >
              Весь трафик идёт на старую версию, правила ниже игнорируются.
            </span>
            <span
              v-else
              class="text-muted"
            >
              Трафик распределяется по правилам ниже.
            </span>
          </div>
          <div
            v-if="killSwitch && killSwitchFlag && killSwitchFlag.updated_at && !killSwitchFlag.updated_by"
            class="small text-danger mb-50"
          >
            Включён {{ formatWhen(killSwitchFlag.updated_at) }} без пользователя: автозащитой edge-роутера (ошибки 5xx новой
            версии) или вручную в БД.
          </div>
          <div class="small text-muted">
            Версия конфига: <strong>{{ version === null ? '—' : version }}</strong>
            · Последнее изменение: <strong>{{ lastChangeText }}</strong>
          </div>
        </div>

        <b-button
          v-if="!killSwitch"
          variant="danger"
          size="lg"
          :disabled="loading || saving"
          @click="$refs.killOnModal.show()"
        >
          Всё на старую версию
        </b-button>
        <b-button
          v-else
          variant="outline-success"
          size="lg"
          :disabled="loading || saving"
          @click="$refs.killOffModal.show()"
        >
          Выключить kill switch
        </b-button>
      </b-card-body>
      <b-card-footer class="d-flex flex-wrap align-items-center">
        <b-form-checkbox
          v-model="watchdog"
          switch
          class="mr-1"
          :disabled="loading || saving"
          @change="toggleWatchdog"
        >
          <strong>Автозащита</strong>
        </b-form-checkbox>
        <span class="small text-muted">
          <template v-if="watchdog">
            Если за 5 минут больше 2% ответов новой версии — ошибки 5xx, роутер сам включит kill switch.
          </template>
          <template v-else>
            Выключена: роутер не переключает сайт на старую версию сам, даже если новая версия падает.
          </template>
          <template v-if="watchdogFlag && watchdogFlag.updated_at">
            Изменено: {{ formatChange(watchdogFlag) }}.
          </template>
        </span>
      </b-card-footer>
    </b-card>

    <div class="d-flex align-items-center justify-content-between mb-50">
      <h5 class="mb-0">
        Правила
      </h5>
      <b-button
        variant="primary"
        size="sm"
        :disabled="saving"
        @click="openCreate"
      >
        Добавить правило
      </b-button>
    </div>

    <b-card
      no-body
      class="mb-1"
    >
      <b-table
        :items="rules"
        :fields="fields"
        striped
        responsive
        small
        :busy="loading"
        empty-text="Правил нет"
        show-empty
        :tbody-tr-class="rowClass"
      >
        <template #cell(pattern)="data">
          <code>{{ data.item.pattern }}</code>
          <div
            v-if="data.item.comment"
            class="text-muted small"
          >
            {{ data.item.comment }}
          </div>
        </template>

        <template #cell(target)="data">
          <b-form-select
            :value="data.item.target"
            :options="targetOptions"
            size="sm"
            :disabled="saving"
            @change="value => changeTarget(data.item, value)"
          />
        </template>

        <template #cell(percent)="data">
          <div class="d-flex align-items-center percent-cell">
            <b-form-input
              :value="data.item.percent"
              type="range"
              min="0"
              max="100"
              step="5"
              :disabled="saving"
              @input="value => $set(percentDrafts, data.item.id, Number(value))"
              @change="value => changePercent(data.item, value)"
            />
            <span class="ml-50 percent-value">{{ percentOf(data.item) }}%</span>
          </div>
        </template>

        <template #cell(enabled)="data">
          <b-form-checkbox
            :checked="data.item.enabled"
            switch
            :disabled="saving"
            @change="value => changeEnabled(data.item, value)"
          />
        </template>

        <template #cell(updated)="data">
          <span class="small">{{ formatChange(data.item) }}</span>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex align-items-center gap-50">
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="saving"
              @click="openEdit(data.item)"
            >
              Изменить
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="saving"
              @click="confirmDelete(data.item)"
            >
              Удалить
            </b-button>
          </div>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner
              small
              class="align-middle mr-1"
            />
            <strong>Загрузка...</strong>
          </div>
        </template>
      </b-table>
      <b-card-footer class="small text-muted">
        Правила проверяются по возрастанию приоритета, срабатывает первое подходящее. Процент — доля посетителей, которые
        попадают на выбранный адресат, остальные идут на другую версию (посетитель «прилипает» к версии cookie <code>fe_ver</code>).
      </b-card-footer>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <h5>Что делать при инциденте</h5>
        <ol class="mb-1 pl-2">
          <li>Нажмите <strong>«Всё на старую версию»</strong> — роутер отправит весь трафик на legacy за ≤ ~5 с, без деплоя.</li>
          <li>
            Проверьте результат: <code>GET /public/frontend-routing</code> должен вернуть <code>"killSwitch": true</code>,
            а заголовок <code>X-FE-Gen</code> на ответах API — начинаться с <code>legacy.</code>.
          </li>
          <li>Точечный откат одного раздела — переключите адресат его правила на <strong>legacy</strong> (та же задержка).</li>
          <li>Если админка недоступна — выполните в БД:</li>
        </ol>
        <pre class="mb-1">UPDATE frontend_flag SET value = '1', updated_at = NOW()
 WHERE `key` = 'frontend.v4.kill_switch';</pre>
        <div class="small text-muted">
          Если отказал сам роутер — вернуть публичный хост напрямую старому приложению (третий уровень отката, §5.3 плана
          <code>docs/nuxt-migration-plan.md</code>).
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      ref="killOnModal"
      title="Включить kill switch?"
      ok-title="Всё на старую версию"
      ok-variant="danger"
      cancel-title="Отмена"
      :ok-disabled="saving"
      :cancel-disabled="saving"
      @ok="evt => toggleKillSwitch(evt, true)"
    >
      <p>
        Весь трафик сайта немедленно (в течение ~5 секунд) уйдёт на <strong>старую версию</strong>.
        Все правила и sticky-cookie будут проигнорированы.
      </p>
      <p class="mb-0 text-muted small">
        Пользователи, которые сейчас на новой версии, увидят старую при следующем переходе. Выключить можно этой же кнопкой.
      </p>
    </b-modal>

    <b-modal
      ref="killOffModal"
      title="Выключить kill switch?"
      ok-title="Выключить"
      ok-variant="success"
      cancel-title="Отмена"
      :ok-disabled="saving"
      :cancel-disabled="saving"
      @ok="evt => toggleKillSwitch(evt, false)"
    >
      <p class="mb-0">
        Трафик снова будет распределяться <strong>по правилам</strong>: всё, что сейчас настроено на v4, вернётся на новую версию.
      </p>
    </b-modal>

    <b-modal
      ref="ruleModal"
      :title="editingRule ? 'Редактирование правила' : 'Новое правило'"
      ok-title="Сохранить"
      cancel-title="Отмена"
      :ok-disabled="saving || !isFormValid"
      :cancel-disabled="saving"
      @ok="saveRule"
    >
      <b-form @submit.prevent>
        <b-form-group
          label="Паттерн"
          label-for="fr-pattern"
          description="Начинается с «/», без пробелов, «?» и «#». «**» — только последним сегментом: /news/**, /delivery, /**"
          :state="patternState"
          :invalid-feedback="patternError"
        >
          <b-form-input
            id="fr-pattern"
            v-model.trim="form.pattern"
            :state="patternState"
            :disabled="saving"
          />
        </b-form-group>

        <b-form-group
          label="Адресат"
          label-for="fr-target"
        >
          <b-form-select
            id="fr-target"
            v-model="form.target"
            :options="targetOptions"
            :disabled="saving"
          />
        </b-form-group>

        <b-form-group
          label="Процент, 0–100"
          label-for="fr-percent"
        >
          <b-form-input
            id="fr-percent"
            v-model.number="form.percent"
            type="number"
            min="0"
            max="100"
            step="1"
            :disabled="saving"
          />
        </b-form-group>

        <b-form-group
          label="Приоритет"
          label-for="fr-priority"
          description="Чем меньше, тем раньше проверяется"
        >
          <b-form-input
            id="fr-priority"
            v-model.number="form.priority"
            type="number"
            min="0"
            max="100000"
            step="1"
            :disabled="saving"
          />
        </b-form-group>

        <b-form-group
          label="Комментарий"
          label-for="fr-comment"
        >
          <b-form-input
            id="fr-comment"
            v-model="form.comment"
            maxlength="255"
            :disabled="saving"
            placeholder="Опционально"
          />
        </b-form-group>

        <b-form-checkbox
          v-model="form.enabled"
          switch
          :disabled="saving"
        >
          Включено
        </b-form-checkbox>
      </b-form>
    </b-modal>

    <b-modal
      ref="deleteModal"
      title="Удалить правило?"
      ok-title="Удалить"
      ok-variant="danger"
      cancel-title="Отмена"
      :ok-disabled="saving"
      :cancel-disabled="saving"
      @ok="deleteRule"
    >
      <div v-if="deletingRule">
        Удалить правило <code>{{ deletingRule.pattern }}</code> → {{ deletingRule.target }} ({{ deletingRule.percent }}%)?
        Запросы, которые оно обслуживало, пойдут по следующим правилам.
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardFooter,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import frontendRoutingApi from '@/services/frontend-routing-api.service'

const KILL_SWITCH_KEY = 'frontend.v4.kill_switch'
const WATCHDOG_KEY = 'frontend.v4.watchdog'

const emptyForm = () => ({
  pattern: '',
  target: 'v4',
  percent: 100,
  priority: 10,
  comment: '',
  enabled: true,
})

// Same rules as the backend (isValidPattern): the server stays the authority, this only saves a round trip.
const validatePattern = pattern => {
  if (!pattern.startsWith('/')) return 'Паттерн должен начинаться с «/»'
  if (/[\s?#]/.test(pattern)) return 'Без пробелов, «?» и «#»'
  const segments = pattern.split('/').filter(Boolean)
  if (!segments.every((segment, index) => segment !== '**' || index === segments.length - 1)) {
    return '«**» допустимо только последним сегментом'
  }
  return ''
}

export default {
  name: 'FrontendRoutingPage',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardFooter,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorText: '',
      version: null,
      killSwitch: false,
      watchdog: true,
      rules: [],
      flags: [],
      percentDrafts: {},
      fields: [
        { key: 'pattern', label: 'Паттерн' },
        { key: 'target', label: 'Адресат', thStyle: { width: '130px' } },
        { key: 'percent', label: 'Процент', thStyle: { width: '220px' } },
        { key: 'priority', label: 'Приоритет' },
        { key: 'enabled', label: 'Включено' },
        { key: 'updated', label: 'Изменено' },
        { key: 'actions', label: 'Действия' },
      ],
      targetOptions: [
        { value: 'legacy', text: 'legacy (старая)' },
        { value: 'v4', text: 'v4 (новая)' },
      ],
      editingRule: null,
      deletingRule: null,
      form: emptyForm(),
    }
  },
  computed: {
    patternError() {
      return this.form.pattern ? validatePattern(this.form.pattern) : ''
    },
    patternState() {
      if (!this.form.pattern) return null
      return this.patternError === ''
    },
    isFormValid() {
      const { pattern, percent, priority } = this.form
      return pattern !== ''
        && validatePattern(pattern) === ''
        && Number.isInteger(percent) && percent >= 0 && percent <= 100
        && Number.isInteger(priority) && priority >= 0 && priority <= 100000
    },
    killSwitchFlag() {
      return this.flags.find(flag => flag.key === KILL_SWITCH_KEY)
    },
    watchdogFlag() {
      return this.flags.find(flag => flag.key === WATCHDOG_KEY)
    },
    lastChangeText() {
      const stamps = [...this.rules, ...this.flags.filter(flag => flag.key === KILL_SWITCH_KEY || flag.key === WATCHDOG_KEY)]
        .filter(item => item.updated_at)
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      return stamps.length ? this.formatChange(stamps[0]) : '—'
    },
  },
  async created() {
    await this.fetchState()
  },
  methods: {
    toastSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Успешно', icon: 'BellIcon', text, variant: 'success',
        },
      })
    },
    toastError(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Ошибка', icon: 'AlertTriangleIcon', text, variant: 'danger',
        },
      })
    },
    getErrorMessage(error) {
      const message = error?.response?.data?.message
      if (Array.isArray(message)) return message.join('; ')
      return message || 'Не удалось выполнить запрос'
    },
    formatWhen(stamp) {
      return new Date(stamp).toLocaleString('ru-RU')
    },
    formatChange(item) {
      if (!item.updated_at) return '—'
      const when = this.formatWhen(item.updated_at)
      return item.updated_by ? `${when}, пользователь #${item.updated_by}` : `${when}, вручную (БД)`
    },
    rowClass(item) {
      return item && !item.enabled ? 'text-muted' : ''
    },
    percentOf(rule) {
      return this.percentDrafts[rule.id] ?? rule.percent
    },
    applyState(state) {
      this.version = state.version
      this.killSwitch = state.killSwitch
      // an API that predates the switch sends no `watchdog`: the router keeps it on then
      this.watchdog = state.watchdog !== false
      this.rules = [...state.rules].sort((a, b) => a.priority - b.priority || a.id - b.id)
      this.flags = state.flags || []
      this.percentDrafts = {}
    },
    async fetchState() {
      this.loading = true
      this.errorText = ''
      try {
        const res = await frontendRoutingApi.getState()
        this.applyState(res.data)
      } catch (e) {
        this.errorText = this.getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
    /** Runs one change and reloads the state, so the page always shows what the backend really has. */
    async mutate(action, successText) {
      if (this.saving) return false
      this.saving = true
      this.errorText = ''
      try {
        await action()
        this.toastSuccess(successText)
        return true
      } catch (e) {
        const text = this.getErrorMessage(e)
        this.errorText = text
        this.toastError(text)
        return false
      } finally {
        await this.fetchState()
        this.saving = false
      }
    },
    changeTarget(rule, target) {
      if (target === rule.target) return
      this.mutate(() => frontendRoutingApi.updateRule(rule.id, { target }), `${rule.pattern} → ${target}`)
    },
    changePercent(rule, value) {
      const percent = Number(value)
      if (percent === rule.percent) {
        this.$delete(this.percentDrafts, rule.id)
        return
      }
      this.mutate(() => frontendRoutingApi.updateRule(rule.id, { percent }), `${rule.pattern}: ${percent}%`)
    },
    changeEnabled(rule, enabled) {
      this.mutate(
        () => frontendRoutingApi.updateRule(rule.id, { enabled }),
        `${rule.pattern}: ${enabled ? 'включено' : 'выключено'}`,
      )
    },
    async toggleKillSwitch(evt, value) {
      evt.preventDefault()
      const ok = await this.mutate(
        () => frontendRoutingApi.setKillSwitch(value),
        value ? 'Kill switch включён: весь трафик на старой версии' : 'Kill switch выключен',
      )
      if (ok) (value ? this.$refs.killOnModal : this.$refs.killOffModal).hide()
    },
    toggleWatchdog(value) {
      this.mutate(
        () => frontendRoutingApi.setWatchdog(value),
        value ? 'Автозащита включена' : 'Автозащита выключена: роутер больше не переключает сайт сам',
      )
    },
    openCreate() {
      this.editingRule = null
      this.form = emptyForm()
      this.$refs.ruleModal.show()
    },
    openEdit(rule) {
      this.editingRule = rule
      this.form = {
        pattern: rule.pattern,
        target: rule.target,
        percent: rule.percent,
        priority: rule.priority,
        comment: rule.comment || '',
        enabled: rule.enabled,
      }
      this.$refs.ruleModal.show()
    },
    async saveRule(evt) {
      evt.preventDefault()
      if (!this.isFormValid) return
      const payload = { ...this.form, comment: this.form.comment || undefined }
      const rule = this.editingRule
      const ok = await this.mutate(
        () => (rule ? frontendRoutingApi.updateRule(rule.id, payload) : frontendRoutingApi.createRule(payload)),
        rule ? 'Правило обновлено' : 'Правило добавлено',
      )
      if (ok) this.$refs.ruleModal.hide()
    },
    confirmDelete(rule) {
      this.deletingRule = rule
      this.$refs.deleteModal.show()
    },
    async deleteRule(evt) {
      evt.preventDefault()
      const ok = await this.mutate(() => frontendRoutingApi.deleteRule(this.deletingRule.id), 'Правило удалено')
      if (ok) this.$refs.deleteModal.hide()
    },
  },
}
</script>

<style scoped>
.percent-cell {
  min-width: 170px;
}
.percent-value {
  min-width: 42px;
  text-align: right;
}
pre {
  background: rgba(0, 0, 0, 0.05);
  padding: 0.75rem;
  border-radius: 0.357rem;
}
</style>
